import { useContext, useEffect, useState } from "react";
import { AccountsV1Service } from "../../../common/services/api/api";
import {
  IAccount,
  IAccountSearch,
  IContact,
} from "../../../common/models/accounts";
import { UserConstants } from "../../../common/constants/user";
import { IoPersonRemove, IoTime } from "react-icons/io5";
import { ImUserCheck } from "react-icons/im";
import { Loading } from "../../../common/components/loading/loading";
import { Themes } from "../../../common/enums/themes";
import Context from "../../../common/components/utils/context";
import ImageWithFallback from "../../../common/components/ImageWithFallbackProps";
import { IoMdPersonAdd } from "react-icons/io";

const ContactsContacts = () => {
  const context = useContext(Context);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isContactsLoading, setIsContactsLoading] = useState(false);
  const [filterResult, setFilterResult] = useState<IContact[]>([]);
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [offers, setOffers] = useState<IContact[]>([]);
  const [isReload, setIsReload] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState<string>("");
  const [searchResult, setSearchResult] = useState<IAccountSearch[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const accountService = new AccountsV1Service();

  const toggleShowModal = () => setShowModal(!showModal);
  const closeModal = () => setShowModal(false);

  useEffect(() => {
    setIsReload(true);
  }, []);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const response = await accountService.get();
        const account: IAccount = response.data;

        // context.setAccount(account);

        const getUniqueByAccountId = <T extends { accountId: string }>(
          items: T[],
        ): T[] => {
          const seen = new Set<string>();
          return items.filter(({ accountId }) => {
            if (seen.has(accountId)) return false;
            seen.add(accountId);
            return true;
          });
        };

        const uniqueRelations = getUniqueByAccountId(account.relations || []);
        const uniqueOffers = getUniqueByAccountId(account.offers || []);
        const combined = [...uniqueRelations, ...uniqueOffers];

        const sortedCombined = [...combined].sort((a, b) =>
          a.username.localeCompare(b.username),
        );

        setContacts(sortedCombined);
        setOffers(
          [...uniqueOffers].sort((a, b) =>
            a.username.localeCompare(b.username),
          ),
        );
        setFilterResult(sortedCombined);
      } catch (error) {
        console.error("Error fetching account:", error);
      } finally {
        setIsContactsLoading(false);
        setIsReload(false);
        setIsLoading(false);
      }
    };

    if (isReload) fetchAccount();
  }, [isReload]);

  const searchAccounts = async (query: string) => {
    if (query.trim().length < 3) {
      setSearchResult([]);
      return;
    }
    try {
      setSearchLoading(true);
      const accountService = new AccountsV1Service();
      const response = await accountService.searchAccounts(query);
      var result = response.data.filter(
        (contact) =>
          !context.account.relations.find(
            (relation) => relation.accountId === contact.accountId,
          ),
      );
      
      //setSearchResult(result);
      setSearchLoading(false);
      return result;
      
    } catch (error) {
      console.error("Error searching accounts", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const addContact = async (accountSearch: IAccountSearch) => {
    if (!context.account || !accountSearch) return;
    try {
      closeModal();
      setIsContactsLoading(true);
      var response = await accountService.addContact(accountSearch.accountId);
      if (response.status === 201 || response.status === 200) {
        context.setAlert({
          message: "Contact was successfully added!",
          theme: Themes.Success,
        });
      }
      setIsReload(true);
    } catch (error) {
      console.error("Error adding contact", error);
    }
  };

  const deleteContact = async (contactId: string) => {
    if (!context.account) return;
    try {
      setIsContactsLoading(true);
      await accountService.deleteContact(contactId);
      setIsReload(true);
      setIsDeleting(false);
      setDeletingContactId("");
    } catch (error) {
      console.error("Error deleting contact", error);
    }
  };

  const confirmRelation = async (accountId: string) => {
    try {
      setIsContactsLoading(true);
      await accountService.confirmContact(accountId);
      setIsReload(true);
    } catch (error) {
      console.error("Error confirming contact", error);
    }
  };

  const filterContacts = (query: string) => {
    if (query.length === 0) {
      setFilterResult(contacts.concat(offers));
      return;
    }
    var result = contacts
      .concat(offers)
      .filter(
        (contact) =>
          contact.username.toLowerCase().includes(query.toLowerCase()) ||
          contact.email.toLowerCase().includes(query.toLowerCase()),
      );
    //setFilterResult(result);
    return result;
  };

  const  performGlobalSearch = async (query: string) => {
    var filterResult = filterContacts(query) || [];
    var searchResult = await searchAccounts(query) || [];

    searchResult = searchResult.filter(
      (account) =>
        !filterResult.find(
          (contact) => contact.accountId === account.accountId,
        ),
    );

    setFilterResult(filterResult);
    setSearchResult(searchResult);
  };

  return (
    <>
      {isLoading && <Loading />}
      {!isLoading && (
        <div className="bg-white min-h-screen">
          {contacts != null && contacts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-500 mb-4">
                Oops! It looks like you don't have any contacts yet.
              </p>
              <p className="text-gray-500 mb-6">
                Start adding some to stay connected!
              </p>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-sm"
                onClick={toggleShowModal}
              >
                add a new contact
              </button>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-6">
                <div className="relative flex-grow max-w-lg">
                  <input
                    type="text"
                    placeholder="Search"
                    onChange={(e) => performGlobalSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {isContactsLoading ? (
                    <tr>
                      <td colSpan={3}>
                        <Loading />
                      </td>
                    </tr>
                  ) : null}
                  {!isContactsLoading &&
                    filterResult.map((contact) => (
                      <tr
                        data-id={contact.accountId}
                        key={contact.accountId}
                        className="border-b border-gray-200"
                      >
                        <td className="py-4 px-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 mr-3">
                              <ImageWithFallback
                                src={
                                  contact.pictureUrl ??
                                  UserConstants.DEFAULT_AVATAR_URL
                                }
                                defaultSrc={UserConstants.DEFAULT_AVATAR_URL}
                                alt={contact.username}
                                className="h-10 w-10 rounded-full"
                              />
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                              {contact.username}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-gray-500">
                          {contact.email}
                        </td>
                        <td>
                          <div className="flex items-center">
                            {contact.status === "pending" &&
                              contact.accountId ===
                                context.account?.accountId && (
                                <div>
                                  <IoTime
                                    title={"Waiting for a confirmation"}
                                    size={20}
                                  />
                                </div>
                              )}
                            {offers.find(
                              (o) => o.accountId === contact.accountId,
                            ) && (
                              <div className="mr-4">
                                <ImUserCheck
                                  title={"Confirm Contact"}
                                  size={20}
                                  className="cursor-pointer"
                                  onClick={() =>
                                    confirmRelation(contact.accountId)
                                  }
                                />
                              </div>
                            )}
                            <div>
                              <IoPersonRemove
                                title={"Delete Contact"}
                                size={20}
                                className="cursor-pointer"
                                onClick={() => {
                                  setIsDeleting(true);
                                  setDeletingContactId(contact.accountId);
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  {!searchLoading &&
                    searchResult?.map(
                      (contact) =>
                        contact.status !== "pending" && (
                          <tr
                            data-id={contact.accountId}
                            key={contact.accountId}
                            className="border-b border-gray-200"
                          >
                            <td className="py-4 px-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 mr-3">
                                  <ImageWithFallback
                                    src={contact.pictureUrl}
                                    defaultSrc={
                                      UserConstants.DEFAULT_AVATAR_URL
                                    }
                                    alt={contact.username}
                                    className="h-10 w-10 rounded-full"
                                  />
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {contact.username}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4 whitespace-nowrap text-sm text-gray-500">
                              {contact.email}
                            </td>
                            <td>
                              <div className="flex items-center">
                                <div>
                                  <IoMdPersonAdd
                                    title={"Add Contact"}
                                    size={20}
                                    className="cursor-pointer"
                                    onClick={() => addContact(contact)}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        ),
                    )}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
            <button
              className="bg-white-600 hover:bg-blue-700 hover:text-white text-blue-600 font-medium py-2 px-2 ms-4 rounded-md text-sm float-right"
              onClick={toggleShowModal}
            >
              Cancel
            </button>
            <div className="flex justify-between items-center mb-6">
              <div className="relative flex-grow max-w-lg">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => performGlobalSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody>
                {isContactsLoading ? (
                  <tr>
                    <td colSpan={3}>
                      <Loading />
                    </td>
                  </tr>
                ) : null}
                {!isContactsLoading &&
                  filterResult.map((contact) => (
                    <tr
                      data-id={contact.accountId}
                      key={contact.accountId}
                      className="border-b border-gray-200"
                    >
                      <td className="py-4 px-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 mr-3">
                            <ImageWithFallback
                              src={contact.pictureUrl || ""}
                              defaultSrc={UserConstants.DEFAULT_AVATAR_URL}
                              alt={contact.username}
                              className="h-10 w-10 rounded-full"
                            />
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {contact.username}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.email}
                      </td>
                      <td>
                        <div className="flex items-center">
                          {contact.status === "pending" &&
                            contact.accountId ===
                              context.account?.accountId && (
                              <div>
                                <IoTime
                                  title={"Waiting for a confirmation"}
                                  size={20}
                                />
                              </div>
                            )}
                          {offers.find(
                            (o) => o.accountId === contact.accountId,
                          ) && (
                            <div className="mr-4">
                              <ImUserCheck
                                title={"Confirm Contact"}
                                size={20}
                                className="cursor-pointer"
                                onClick={() =>
                                  confirmRelation(contact.accountId)
                                }
                              />
                            </div>
                          )}
                          <div>
                            <IoPersonRemove
                              title={"Delete Contact"}
                              size={20}
                              className="cursor-pointer"
                              onClick={() => {
                                setIsDeleting(true);
                                setDeletingContactId(contact.accountId);
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                {!searchLoading &&
                  searchResult?.map(
                    (contact) =>
                      contact.status !== "pending" && (
                        <tr
                          data-id={contact.accountId}
                          key={contact.accountId}
                          className="border-b border-gray-200"
                        >
                          <td
                            colSpan={2}
                            className="py-4 px-4 whitespace-nowrap"
                          >
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 mr-3">
                                <ImageWithFallback
                                  src={contact.pictureUrl}
                                  defaultSrc={UserConstants.DEFAULT_AVATAR_URL}
                                  alt={contact.username}
                                  className="h-10 w-10 rounded-full"
                                />
                              </div>
                              <div className="text-sm font-medium text-gray-900">
                                {contact.username}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center">
                              <div>
                                <IoMdPersonAdd
                                  title={"Add Contact"}
                                  size={20}
                                  className="cursor-pointer"
                                  onClick={() => addContact(contact)}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ),
                  )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h2 className="text-xl font-semibold mb-4">Delete Contact?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this contact?
            </p>
            <div className="flex justify-end">
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md mr-2 text-sm"
                onClick={() => {
                  setIsDeleting(false);
                  setDeletingContactId("");
                }}
              >
                Cancel
              </button>
              <button
                className={`bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md text-sm ${
                  !deletingContactId ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={!deletingContactId}
                onClick={() =>
                  deletingContactId && deleteContact(deletingContactId)
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactsContacts;
